import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "./../child/Alert/SuccessAlert";
import DengerAlert from "./../child/Alert/DengerAlert";
import { Icon } from "@iconify/react/dist/iconify.js";

const ResetPasswordLayer = () => {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    noPersonalInfo: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = localStorage.getItem("signinemail");
    setEmail(storedEmail || "");

    const token = localStorage.getItem("authToken");
    if (token) navigate("/dashboard");

    document.body.classList.add("signin-page");
    return () => {
      document.body.classList.remove("signin-page");
    };
  }, [navigate]);

  const validateLive = (newPass, confirmPass) => {
    const validationErrors = {};

    const hasLength = newPass.length >= 8 && newPass.length <= 64;
    const hasUpper = /[A-Z]/.test(newPass);
    const hasLower = /[a-z]/.test(newPass);
    const hasNumber = /[0-9]/.test(newPass);
    const hasSpecial = /[!@#$%^&*]/.test(newPass);
    const hasNoPersonalInfo =
      newPass &&
      !newPass.toLowerCase().includes(email?.toLowerCase()) &&
      !newPass.toLowerCase().includes(email?.split("@")[0]?.toLowerCase());

    const rules = {
      length: hasLength,
      uppercase: hasUpper,
      lowercase: hasLower,
      number: hasNumber,
      special: hasSpecial,
      noPersonalInfo: hasNoPersonalInfo,
    };
    setPasswordRules(rules);

    const allRulesValid = Object.values(rules).every(Boolean);
    const passwordsMatch = newPass === confirmPass;

    if (!newPass) {
      validationErrors.newPassword = "New password is required";
    } 

    if (!confirmPass) {
      validationErrors.confirmPassword = "Confirm password is required";
    } else if (!passwordsMatch) {
      validationErrors.confirmPassword = "The new password and confirm password does not match";
    }

    setErrors(validationErrors);
    setFormReady(allRulesValid && passwordsMatch);
    setShowRules(newPass.length > 0);
  };

  const getRuleClass = (isValid) => (isValid ? "text-success" : "text-danger");

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const handleResetPass = async (e) => {
    e.preventDefault();
    if (!formReady) return;

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/resetpassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: localStorage.getItem("adminId"),
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (data.status === "403") {
        setErrors({ newPassword: data.message });
      }else {
        localStorage.setItem("email", email);
        setSuccessMessage("Your password has been updated successfully");
        setTimeout(() => {
          setSuccessMessage("");
          navigate("/sign-in");
        }, 3000);
      }
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      {successMessage && <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />}
      {errorMessage && <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />}

      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src={`${process.env.REACT_APP_PUBLIC_UR}/assets/images/auth/reset-password.png`} alt="Auth" />
        </div>
      </div>

      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <img src={`${process.env.REACT_APP_PUBLIC_UR}/assets/images/logo.svg`} alt="Logo"   style={{ height: '5.5rem' }}/>
          <h4 className="mb-12">Reset Password</h4>

          <form onSubmit={handleResetPass}>
            {/* New Password */}
            <div className="mb-20">
              <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                New Password <span className="text-danger-600">*</span>
              </label>
              <div className="position-relative">
                 <div className="icon-field">
                  <span className="icon top-50 translate-middle-y">
                                  <Icon icon="solar:lock-password-outline" />
                    </span>
                <input
                  type={passwordVisible ? "text" : "password"}
                  className="form-control"
                  placeholder="Enter New Password"
                  maxLength="64"
                  value={newPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewPassword(value);
                    validateLive(value, confirmPassword);
                  }}
                  onFocus={() => setShowRules(true)}
                />
                </div>
                <span
                  className={`toggle-password ${passwordVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                  onClick={togglePasswordVisibility}
                ></span>
              </div>
              {showRules && (
                <ul style={{ fontSize: "12px", paddingLeft: "20px" }}>
                  <li className={getRuleClass(passwordRules.length)}>8–64 characters</li>
                  <li className={getRuleClass(passwordRules.uppercase)}>At least one uppercase letter</li>
                  <li className={getRuleClass(passwordRules.lowercase)}>At least one lowercase letter</li>
                  <li className={getRuleClass(passwordRules.number)}>At least one number</li>
                  <li className={getRuleClass(passwordRules.special)}>At least one special character (!@#$%^&*)</li>
                  <li className={getRuleClass(passwordRules.noPersonalInfo)}>No email/name included</li>
                </ul>
              )}
              {errors.newPassword && <p className="text-danger mt-1">{errors.newPassword}</p>}
            </div>

            {/* Confirm Password */}
            <div className="mb-20">
              <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                Confirm Password <span className="text-danger-600">*</span>
              </label>
              <div className="position-relative">
                 <div className="icon-field">
                  <span className="icon top-50 translate-middle-y">
                                  <Icon icon="solar:lock-password-outline" />
                    </span>
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  className="form-control"
                  placeholder="Enter Confirm Password"
                  maxLength="64"
                  value={confirmPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setConfirmPassword(value);
                    validateLive(newPassword, value);
                  }}
                />
                </div>
                <span
                  className={`toggle-password ${confirmPasswordVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                  onClick={toggleConfirmPasswordVisibility}
                ></span>
              </div>
              {errors.confirmPassword && <p className="text-danger mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
              disabled={loading || !formReady}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ResetPasswordLayer;
